import {safelyGetStorageItem} from '@vio/core/src/_web/utils/persistence/safelyGetStorageItem'
import {safelyRemoveStorageItem} from '@vio/core/src/_web/utils/persistence/safelyRemoveStorageItem'
import {safelySetStorageItem} from '@vio/core/src/_web/utils/persistence/safelySetStorageItem'
import {safeJsonParse} from '@vio/core/src/utils/object'

import {DF_RESUME_STORAGE_TTL_MS} from '../stash/constants'

// localStorage, not sessionStorage: OAuth can complete in a different tab.
export const DF_FREEZE_INTENT_STORAGE_KEY = 'DF_FREEZE_INTENT'

export type FreezeIntent = {
  hotelId: string
  /** The pre-sign-in offer id. Post-auth searches regenerate offer ids, so
   * this is kept for observability only and must never be matched against. */
  offerId?: string
  fromOnboarding?: boolean
  /** False when the traveler only asked to sign in, so the resume opens the sheet instead of freezing. */
  committed: boolean
  createdAt: number
}

/**
 * Records that a guest started sign-in from the freeze sheet, and whether
 * they committed to freezing (clicked the in-overlay CTA) or only asked to sign in.
 * @param intent - The hotel scope, pre-sign-in offer id and commitment
 * @param now - Injectable clock for tests
 */
export const writeFreezeIntent = (
  intent: {
    hotelId: string
    offerId?: string
    fromOnboarding?: boolean
    committed?: boolean
  },
  now: number = Date.now()
): void => {
  safelySetStorageItem(
    'localStorage',
    DF_FREEZE_INTENT_STORAGE_KEY,
    JSON.stringify({...intent, createdAt: now}),
    'writeFreezeIntent failed'
  )
}

/**
 * Reads the pending freeze intent, treating an expired or malformed value
 * as absent.
 * @param now - Injectable clock for tests
 * @returns The pending intent, or null when none is pending
 */
export const readFreezeIntent = (
  now: number = Date.now()
): FreezeIntent | null => {
  const raw = safelyGetStorageItem(
    'localStorage',
    DF_FREEZE_INTENT_STORAGE_KEY,
    'readFreezeIntent failed'
  )
  if (!raw) return null

  const intent = safeJsonParse<FreezeIntent>(raw)
  if (
    intent?.hotelId &&
    typeof intent.createdAt === 'number' &&
    now - intent.createdAt < DF_RESUME_STORAGE_TTL_MS
  ) {
    return {...intent, committed: intent.committed ?? true}
  }

  clearFreezeIntent()
  return null
}

/** Clears the pending freeze intent. */
export const clearFreezeIntent = (): void => {
  safelyRemoveStorageItem(
    'localStorage',
    DF_FREEZE_INTENT_STORAGE_KEY,
    'clearFreezeIntent failed'
  )
}
